import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { cn } from '../../utils.js'

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> {
  /** Visible label text */
  label: string
  /** Place label before or after the toggle */
  labelPosition?: 'before' | 'after'
}

/**
 * Accessible on/off toggle switch with label.
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, labelPosition = 'after', className, id: externalId, checked, ...props },
  ref,
) {
  const generatedId = useId()
  const id = externalId ?? generatedId

  const switchEl = (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-neutral-300)]',
      )}
      onClick={() => {
        // Trigger the hidden input's change
        const input = document.getElementById(id) as HTMLInputElement | null
        if (input && !props.disabled) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            HTMLInputElement.prototype,
            'checked',
          )?.set
          nativeInputValueSetter?.call(input, !checked)
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }}
      disabled={props.disabled}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  )

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={checked}
        className="sr-only"
        {...props}
      />
      {labelPosition === 'before' && (
        <label
          id={`${id}-label`}
          htmlFor={id}
          className="text-sm text-[var(--color-text)] select-none cursor-pointer"
        >
          {label}
        </label>
      )}
      {switchEl}
      {labelPosition === 'after' && (
        <label
          id={`${id}-label`}
          htmlFor={id}
          className="text-sm text-[var(--color-text)] select-none cursor-pointer"
        >
          {label}
        </label>
      )}
    </div>
  )
})
